/* POVA — HIOS scroll-motion layer
 * ---------------------------------------------------------------------------
 * Premium scroll choreography for the HIOS page ONLY. Runs exclusively when
 * <main class="hios"> is present, so it never touches any other template.
 *
 * Effects (all additive, all gated behind prefers-reduced-motion: reduce):
 *   1. Lenis smooth inertia scroll (+ RAF loop, cleanup on navigation away)
 *   2. Scroll progress bar (scaleX driven by scroll progress)
 *   3. Intro curtain (black overlay that lifts on load)
 *   4. Hero entrance (eyebrow fade/rise; logo + phones blur-in, keyframe-based
 *      and load-gated so the reveal always plays and never jumps to sharp)
 *   5. Split-text headings (word-by-word masked rise + slight rotate)
 *   6. Scroll reveals (sub-text + media fade + rise via IntersectionObserver)
 *   7. Image reveals (clip-wipe + zoom-settle inside an overflow:hidden wrapper)
 *      — reveals that carry an image are load-gated: the reveal waits until the
 *        image has loaded (and hints a lazy image to load now) so a reveal never
 *        plays on a blank / late-loading image.
 *   8. Parallax (subtle translateY on the hero's own top glow only)
 *   9/10. Card hover shine + ambient breathe/float -> handled in section CSS
 *
 * When prefers-reduced-motion is set: no Lenis (native scroll kept, with CSS
 * scroll-snap intact), no curtain, and the `hios-anim` hook is NOT added, so
 * every initial-hidden rule in the stylesheet is inert and content is fully
 * visible immediately.
 *
 * Signature easing is defined in CSS as --hios-ease: cubic-bezier(.22,1,.36,1).
 * ------------------------------------------------------------------------- */
(function () {
  "use strict";

  var root = document.querySelector("main.hios");
  if (!root) return; // HIOS page only — hard gate.

  var reduceMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
  var motionOK = !reduceMQ.matches;

  var docEl = document.documentElement;
  docEl.classList.add("hios-js");
  if (motionOK) docEl.classList.add("hios-anim");

  /* --- teardown registry ------------------------------------------------- */
  var cleanups = [];
  function onCleanup(fn) { cleanups.push(fn); }
  function cleanupAll() {
    while (cleanups.length) { try { cleanups.pop()(); } catch (e) {} }
  }
  // Real navigation away -> tear down. bfcache (persisted) keeps the instance
  // so a Back navigation restores the live, already-initialised page.
  window.addEventListener("pagehide", function (e) {
    if (!e.persisted) cleanupAll();
  });

  /* --- helpers ----------------------------------------------------------- */
  function make(tag, cls) {
    var el = document.createElement(tag);
    if (cls) el.className = cls;
    el.setAttribute("aria-hidden", "true");
    return el;
  }

  /* === 2. Scroll progress bar =========================================== */
  // Appended inside .hios so the stylesheet's --acc resolves on it.
  var progress = make("div", "hios-progress");
  root.appendChild(progress);
  function setProgress() {
    var max = docEl.scrollHeight - window.innerHeight;
    var p = max > 0 ? (window.scrollY || window.pageYOffset) / max : 0;
    progress.style.transform = "scaleX(" + Math.max(0, Math.min(1, p)) + ")";
  }

  /* === 8. Parallax (ambient glows only — never product renders) ========= */
  var parallaxEls = [];
  function collectParallax() {
    parallaxEls = [].slice.call(root.querySelectorAll("[data-hios-parallax]"));
  }
  function updateParallax() {
    if (!motionOK) return;
    var vh = window.innerHeight;
    for (var i = 0; i < parallaxEls.length; i++) {
      var el = parallaxEls[i];
      var rect = el.getBoundingClientRect();
      var center = rect.top + rect.height / 2;
      var rel = (center - vh / 2) / vh; // ~[-1, 1] across the viewport
      var speed = parseFloat(el.getAttribute("data-hios-parallax")) || 40;
      el.style.setProperty("--hios-py", (rel * speed).toFixed(1) + "px");
    }
  }

  function onScroll() {
    setProgress();
    updateParallax();
  }

  /* === 1. Lenis smooth inertia scroll =================================== */
  var lenis = null;
  if (motionOK && typeof window.Lenis === "function") {
    lenis = new window.Lenis({
      duration: 1.1,
      // ease-out expo — long, premium glide.
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      // Keep native behaviour on horizontal scrollers and the mobile nav drawer
      // so the carousel, menu, and any inner scroll regions still work.
      prevent: function (node) {
        return !!(node.closest && node.closest(
          ".hios-row, .nav-drawer, [data-lenis-prevent]"
        ));
      }
    });

    var rafId = 0;
    function raf(time) { lenis.raf(time); rafId = requestAnimationFrame(raf); }
    rafId = requestAnimationFrame(raf);
    lenis.on("scroll", onScroll);

    onCleanup(function () {
      cancelAnimationFrame(rafId);
      lenis.off("scroll", onScroll);
      lenis.destroy();
      lenis = null;
    });

    // In-page anchors -> Lenis-driven scroll (preserves smoothness).
    var anchors = [].slice.call(root.querySelectorAll('a[href^="#"]'));
    anchors.forEach(function (a) {
      var handler = function (ev) {
        var id = a.getAttribute("href");
        if (!id || id === "#") return;
        var target = document.querySelector(id);
        if (!target) return;
        ev.preventDefault();
        lenis.scrollTo(target, { offset: -24 });
      };
      a.addEventListener("click", handler);
      onCleanup(function () { a.removeEventListener("click", handler); });
    });

    // Pause Lenis while the mobile nav drawer (body.nav-open, from js-motion.js)
    // is open, then resume — avoids background scroll fighting the drawer.
    var navObs = new MutationObserver(function () {
      if (!lenis) return;
      if (document.body.classList.contains("nav-open")) lenis.stop();
      else lenis.start();
    });
    navObs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    onCleanup(function () { navObs.disconnect(); });
  } else {
    // Fallback: native smooth scroll (CSS scroll-snap stays active), progress
    // bar + parallax still update on the native scroll event.
    if (motionOK) docEl.style.scrollBehavior = "smooth";
    window.addEventListener("scroll", onScroll, { passive: true });
    onCleanup(function () { window.removeEventListener("scroll", onScroll); });
  }

  /* === 3. Intro curtain ================================================= */
  if (motionOK) {
    var curtain = make("div", "hios-curtain");
    root.appendChild(curtain);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { if (curtain) curtain.classList.add("is-lifted"); });
    });
    var dropCurtain = function () {
      if (curtain && curtain.parentNode) curtain.parentNode.removeChild(curtain);
      curtain = null;
    };
    curtain.addEventListener("transitionend", dropCurtain);
    setTimeout(dropCurtain, 1600); // safety net if transitionend never fires
  }

  /* === 4. Hero entrance ================================================= */
  var hero = root.querySelector(".hios-hero");
  if (hero) {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { hero.classList.add("is-ready"); });
    });
  }

  /* === 4b. Hero media blur-in ========================================== */
  // The hero logo + phones reveal is a CSS @keyframes animation (full-duration,
  // cannot "jump" to sharp), triggered ONLY once each image has actually loaded
  // and painted — so a cached image still shows the blurred first frame animate
  // out. Gated by motionOK; under reduced-motion the images are never hidden.
  if (motionOK && hero) {
    var heroImgs = [].slice.call(hero.querySelectorAll(
      ".hios-hero-logo img, .hios-hero-phones img, .hios-hero-m img"
    ));
    heroImgs.forEach(function (img) {
      var played = false;
      function play() {
        if (played) return;
        played = true;
        // Double-rAF guarantees the blurred initial frame paints before the
        // animation class is added, so the keyframe runs its whole duration.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { img.classList.add("hios-img-in"); });
        });
      }
      if (img.complete && img.naturalWidth > 0) {
        play();
      } else {
        img.addEventListener("load", play, { once: true });
        img.addEventListener("error", play, { once: true });
      }
      // Safety net: never leave a hero image stuck hidden if load never fires.
      setTimeout(play, 3000);
    });
  }

  /* === 5. Split-text headings =========================================== */
  // Wrap each word in an overflow-hidden span with a rising inner span.
  // Skipped when the heading contains markup (keeps e.g. inline <b> intact).
  function splitHeading(el) {
    if (el.dataset.hiosSplit === "done") return;
    if (el.children.length > 0) { el.classList.add("hios-reveal"); return; }
    var words = el.textContent.split(/(\s+)/);
    var frag = document.createDocumentFragment();
    var wi = 0;
    words.forEach(function (w) {
      if (/^\s+$/.test(w) || w === "") { frag.appendChild(document.createTextNode(w)); return; }
      var outer = document.createElement("span");
      outer.className = "hios-split-word";
      var inner = document.createElement("span");
      inner.className = "hios-split-inner";
      inner.textContent = w;
      inner.style.transitionDelay = (wi * 0.07).toFixed(3) + "s";
      outer.appendChild(inner);
      frag.appendChild(outer);
      wi++;
    });
    el.textContent = "";
    var line = document.createElement("span");
    line.className = "hios-split-line";
    line.appendChild(frag);
    el.appendChild(line);
    el.classList.add("hios-splittext");
    el.dataset.hiosSplit = "done";
  }

  /* === 6 + 7. Reveals & image reveals via IntersectionObserver ========== */
  var io = null;
  if (motionOK && "IntersectionObserver" in window) {
    // Reveal once in view. If the element carries an image that hasn't loaded
    // yet (lazy section media), hint it to load now and HOLD the reveal until
    // the image is ready — so a reveal never plays on a blank / late image.
    function revealWhenReady(el) {
      var img = (el.matches && el.matches("img")) ? el
              : (el.querySelector ? el.querySelector("img") : null);
      if (!img || (img.complete && img.naturalWidth > 0)) { el.classList.add("is-in"); return; }
      try { img.loading = "eager"; if ("fetchPriority" in img) img.fetchPriority = "high"; } catch (e) {}
      var fire = function () { el.classList.add("is-in"); };
      img.addEventListener("load", fire, { once: true });
      img.addEventListener("error", fire, { once: true });
      setTimeout(fire, 2500); // safety: never hold a reveal hidden too long
    }
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target); // reveal once
        revealWhenReady(entry.target);
      });
    }, { rootMargin: "0px 0px 8% 0px", threshold: 0.12 });

    // Headings -> split-text (hero <h1> too).
    [].slice.call(root.querySelectorAll(".hios-title, .hios-h2")).forEach(function (h) {
      splitHeading(h);
      io.observe(h);
    });

    // Sub-text + whole-card fade/rise.
    [].slice.call(root.querySelectorAll(".hios-sub, .hios-card, .hios-marquee"))
      .forEach(function (el) { el.classList.add("hios-reveal"); io.observe(el); });

    // Large media -> clip/scale reveal (wrapper already clips, or CSS adds it).
    [].slice.call(root.querySelectorAll(
      ".hios-split-media, .hios-rowimg, .hios-cards.c2 .hios-media, .hios-stack .hios-split-media"
    )).forEach(function (el) { el.classList.add("hios-reveal-img"); io.observe(el); });

    onCleanup(function () { io.disconnect(); });
  }

  /* === 8b. Flag parallax target (hero top glow only) ==================== */
  // The per-section ambient orange glows were removed at the owner's request;
  // only the hero's own top glow drifts on scroll now. The mouse-follow .field
  // halo is owned elsewhere and left untouched.
  if (motionOK) {
    if (hero) hero.setAttribute("data-hios-parallax", "60");
    collectParallax();
  }

  /* === 11. Carousel — pointer drag-to-scroll (enhances, never replaces) == */
  // The arrow buttons + native overflow scrolling already work; this only adds
  // click-drag on pointer devices. A movement threshold keeps image/button
  // clicks intact.
  if (motionOK) {
    [].slice.call(root.querySelectorAll(".hios-carousel .hios-row")).forEach(function (row) {
      var down = false, moved = false, startX = 0, startScroll = 0;
      function pd(e) {
        if (e.button !== undefined && e.button !== 0) return;
        down = true; moved = false;
        startX = e.clientX; startScroll = row.scrollLeft;
      }
      function pm(e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 6) moved = true;
        if (moved) { row.scrollLeft = startScroll - dx; }
      }
      function pu() {
        if (moved) row.classList.add("is-dragged");
        down = false;
        setTimeout(function () { row.classList.remove("is-dragged"); }, 0);
      }
      row.addEventListener("pointerdown", pd);
      window.addEventListener("pointermove", pm, { passive: true });
      window.addEventListener("pointerup", pu);
      // Swallow the click that follows a drag so a drag never triggers a link.
      row.addEventListener("click", function (e) {
        if (row.classList.contains("is-dragged")) { e.preventDefault(); e.stopPropagation(); }
      }, true);
      onCleanup(function () {
        row.removeEventListener("pointerdown", pd);
        window.removeEventListener("pointermove", pm);
        window.removeEventListener("pointerup", pu);
      });
    });
  }

  /* --- initial paint & resize ------------------------------------------- */
  setProgress();
  updateParallax();
  var onResize = function () { collectParallax(); updateParallax(); };
  window.addEventListener("resize", onResize, { passive: true });
  onCleanup(function () { window.removeEventListener("resize", onResize); });
})();
